<script setup>
import { ref, provide } from 'vue'

/**
 * Dropdown — drop-in replacement for BV2's b-dropdown.
 *
 * Props:
 *   text        — string, button label (also available via #button-content slot)
 *   right       — boolean, right-align the dropdown menu
 *   noCaret     — boolean, hide the toggle caret
 *   variant     — string, button variant (adds btn-{variant} class; default 'secondary')
 *   toggleClass — string | array, extra class(es) applied to the toggle button
 *
 * Exposes:
 *   hide() — programmatically close the dropdown
 *   show() — programmatically open the dropdown
 */
defineProps({
    text:        { type: String,  default: '' },
    right:       { type: Boolean, default: false },
    noCaret:     { type: Boolean, default: false },
    disabled:    { type: Boolean, default: false },
    fill:        { type: Boolean, default: false },
    dropup:      { type: Boolean, default: false },
    variant:     { type: String,  default: 'secondary' },
    toggleClass: { default: '' },
})

const emit = defineEmits(['show', 'hide'])

const isOpen = ref(false)

function close() {
    if (!isOpen.value) return
    isOpen.value = false
    emit('hide')
}
function open() {
    isOpen.value = true
    emit('show')
}
function toggle() { isOpen.value ? close() : open() }

provide('closeDropdown', close)

defineExpose({ hide: close, show: open })
</script>

<template>
    <div class="dropdown" :class="{'show': isOpen, 'dropup': dropup}" v-click-outside="close">
        <ul class="dropdown-menu" :class="{'show': isOpen, 'dropdown-menu-right': right}">
            <slot />
        </ul>
        <button
            type="button"
            :class="['btn', `btn-${variant}`, 'dropdown-toggle', {'dropdown-no-caret': noCaret}, toggleClass]"
            :style="fill ? 'position: absolute; inset: 0;' : ''"
            :disabled="disabled"
            @click="toggle"
        >
            <slot name="button-content">{{ text }}</slot>
        </button>
    </div>
</template>

<style scoped>
.dropdown-no-caret::after {
    display: none !important;
}
</style>
